import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { api } from '@/App';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Settings, Truck } from 'lucide-react';

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'user',
    hospital_id: '',
    region_id: '',
    address: '',
    post_nr: '',
    city: '',
    receive_orders: false,
    receive_order_copy: true,
    receive_region_orders: false
  });
  const [manageItemsOpen, setManageItemsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userItems, setUserItems] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, hospitalsRes, vendorsRes, regionsRes] = await Promise.all([
        api.get('/users'),
        api.get('/hospitals'),
        api.get('/vendors'),
        api.get('/regions')
      ]);
      setUsers(usersRes.data);
      setHospitals(hospitalsRes.data);
      setVendors(vendorsRes.data);
      setRegions(regionsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await api.put(`/users/${editingId}`, updateData);
        toast.success('User updated');
      } else {
        await api.post('/auth/register', formData);
        toast.success('User created');
      }
      setOpen(false);
      setFormData({
        email: '',
        name: '',
        password: '',
        role: 'user',
        hospital_id: '',
        region_id: '',
        address: '',
        post_nr: '',
        city: '',
        receive_orders: false,
        receive_order_copy: true,
        receive_region_orders: false
      });
      setEditingId(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setFormData({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role,
      hospital_id: user.hospital_id || '',
      region_id: user.region_id || '',
      address: user.address || '',
      post_nr: user.post_nr || '',
      city: user.city || '',
      receive_orders: user.receive_orders || false,
      receive_order_copy: user.receive_order_copy !== undefined ? user.receive_order_copy : true,
      receive_region_orders: user.receive_region_orders || false
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success('User deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleManageItems = async (user) => {
    setSelectedUser(user);
    try {
      // Load master items, user items, and hospitals in parallel
      const [masterItemsRes, userItemsRes, hospitalsRes] = await Promise.all([
        api.get('/master-items'),
        api.get(`/user-items/${user.id}`),
        api.get('/hospitals')
      ]);
      
      const allItems = masterItemsRes.data;
      const userAssignments = userItemsRes.data;
      const hospitalsList = hospitalsRes.data;
      
      // Get the user's hospital and its allowed vendors
      const userHospital = hospitalsList.find(h => h.id === user.hospital_id);
      const allowedVendorIds = userHospital?.allowed_vendor_ids || [];
      
      // Filter items by hospital's allowed vendors (if any are specified)
      const filteredItems = allowedVendorIds.length > 0
        ? allItems.filter(item => allowedVendorIds.includes(item.vendor_id))
        : allItems; // If no vendors specified, show all
      
      // Create a map of user assignments by item_id
      const userItemMap = userAssignments.reduce((acc, userItem) => {
        acc[userItem.item_id] = userItem;
        return acc;
      }, {});
      
      // Merge filtered master items with user assignment status
      const enrichedItems = filteredItems.map(masterItem => {
        const userAssignment = userItemMap[masterItem.id];
        return {
          ...masterItem,
          // User assignment data
          user_item_id: userAssignment?.id || null,
          enabled: userAssignment?.enabled || false,
          order_count: userAssignment?.order_count || 0,
          is_assigned: !!userAssignment,
          // Master item data for display
          item_id: masterItem.id,
          item_name: masterItem.name,
          item_description: masterItem.description,
          sku: masterItem.sku,
          units: masterItem.units,
          // Vendor data from master item
          vendor_id: masterItem.vendor_id
        };
      });
      
      setUserItems(enrichedItems);
      setManageItemsOpen(true);
    } catch (error) {
      console.error('Error loading items:', error);
      toast.error('Failed to load items: ' + (error.response?.data?.detail || error.message));
    }
  };

  const toggleItemEnabled = async (item, currentState) => {
    try {
      if (item.is_assigned) {
        // Item is already assigned to user - toggle its enabled state
        await api.put(`/user-items/${item.user_item_id}`, { enabled: !currentState });
        toast.success(`Item ${!currentState ? 'enabled' : 'disabled'}`);
      } else {
        // Item not assigned to user - create new assignment
        await api.post(`/users/${selectedUser.id}/items`, { 
          item_id: item.item_id,
          enabled: true 
        });
        toast.success('Item added and enabled for user');
      }
      
      // Refresh the items list
      await handleManageItems(selectedUser);
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const groupedUserItems = useMemo(() => {
    if (!userItems.length || !vendors.length) {
      return {};
    }
    
    return userItems.reduce((acc, item) => {
      // Get vendor name from vendors list
      const vendor = vendors.find(v => v.id === item.vendor_id);
      const vendorName = vendor?.name || 'Unknown Vendor';
      
      if (!acc[vendorName]) {
        acc[vendorName] = [];
      }
      acc[vendorName].push(item);
      return acc;
    }, {});
  }, [userItems, vendors]);

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <>
      <Card className="shadow-lg border-0" data-testid="users-card">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl flex items-center space-x-2">
                <Users className="w-6 h-6 text-blue-600" />
                <span>Users</span>
              </CardTitle>
              <CardDescription>Manage user accounts</CardDescription>
            </div>
            <Dialog open={open} onOpenChange={(isOpen) => {
              setOpen(isOpen);
              if (!isOpen) {
                setEditingId(null);
                setFormData({
                  email: '',
                  name: '',
                  password: '',
                  role: 'user',
                  hospital_id: '',
                  region_id: '',
                  address: '',
                  post_nr: '',
                  city: '',
                  receive_orders: false,
                  receive_order_copy: true,
                  receive_region_orders: false
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg" data-testid="add-user-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="user-dialog">
                <form onSubmit={handleSubmit}>
                  <DialogHeader>
                    <DialogTitle>{editingId ? 'Edit User' : 'Add User'}</DialogTitle>
                    <DialogDescription>Enter user details</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Doe"
                        required
                        data-testid="user-name-input"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="user@example.com"
                        required
                        data-testid="user-email-input"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="password">Password {editingId && '(leave blank to keep current)'}</Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="••••••••"
                        required={!editingId}
                        data-testid="user-password-input"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="role">Role</Label>
                      <Select
                        value={formData.role}
                        onValueChange={(value) => setFormData({ 
                          ...formData, 
                          role: value,
                          // Clear role-specific fields when changing role
                          hospital_id: value === 'user' ? formData.hospital_id : '',
                          region_id: value === 'driver' ? formData.region_id : '',
                          receive_orders: value === 'admin' ? formData.receive_orders : false,
                          receive_region_orders: value === 'driver' ? formData.receive_region_orders : false
                        })}
                      >
                        <SelectTrigger className="mt-2" data-testid="user-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="driver">Driver</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Street address"
                        data-testid="user-address-input"
                        className="mt-2"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="post_nr">Post Nr.</Label>
                        <Input
                          id="post_nr"
                          value={formData.post_nr}
                          onChange={(e) => setFormData({ ...formData, post_nr: e.target.value })}
                          placeholder="1234"
                          data-testid="user-post-nr-input"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          placeholder="Copenhagen"
                          data-testid="user-city-input"
                          className="mt-2"
                        />
                      </div>
                    </div>
                    {formData.role === 'user' && (
                      <div>
                        <Label htmlFor="hospital">Hospital</Label>
                        <Select
                          value={formData.hospital_id}
                          onValueChange={(value) => setFormData({ ...formData, hospital_id: value })}
                        >
                          <SelectTrigger className="mt-2" data-testid="user-hospital-select">
                            <SelectValue placeholder="Select hospital" />
                          </SelectTrigger>
                          <SelectContent>
                            {hospitals.map((hospital) => (
                              <SelectItem key={hospital.id} value={hospital.id}>
                                {hospital.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {/* Driver-specific: Region selection */}
                    {formData.role === 'driver' && (
                      <div>
                        <Label htmlFor="region">Region</Label>
                        <Select
                          value={formData.region_id}
                          onValueChange={(value) => setFormData({ ...formData, region_id: value })}
                        >
                          <SelectTrigger className="mt-2" data-testid="user-region-select">
                            <SelectValue placeholder="Select region" />
                          </SelectTrigger>
                          <SelectContent>
                            {regions.map((region) => (
                              <SelectItem key={region.id} value={region.id}>
                                {region.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {formData.role === 'admin' && (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="receive_orders"
                          checked={formData.receive_orders}
                          onCheckedChange={(checked) => setFormData({ ...formData, receive_orders: checked })}
                          data-testid="user-receive-orders-checkbox"
                        />
                        <Label htmlFor="receive_orders" className="cursor-pointer">
                          Receive order emails
                        </Label>
                      </div>
                    )}
                    
                    {/* Driver-specific: Receive region orders checkbox */}
                    {formData.role === 'driver' && (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="receive_region_orders"
                          checked={formData.receive_region_orders}
                          onCheckedChange={(checked) => setFormData({ ...formData, receive_region_orders: checked })}
                          data-testid="user-receive-region-orders-checkbox"
                        />
                        <Label htmlFor="receive_region_orders" className="cursor-pointer">
                          Receive order emails for this region
                        </Label>
                      </div>
                    )}
                    
                    {/* Receive Order Copy Checkbox - only for regular users (not admins) */}
                    {formData.role === 'user' && (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="receive_order_copy"
                          checked={formData.receive_order_copy}
                          onCheckedChange={(checked) => setFormData({ ...formData, receive_order_copy: checked })}
                          data-testid="user-receive-order-copy-checkbox"
                        />
                        <Label htmlFor="receive_order_copy" className="cursor-pointer">
                          Receive order copy
                        </Label>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="save-user-button">
                      {editingId ? 'Update' : 'Create'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">No users yet</p>
              <p className="text-sm">Add your first user to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => {
                const hospital = hospitals.find(h => h.id === user.hospital_id);
                const region = regions.find(r => r.id === user.region_id);
                return (
                  <div
                    key={user.id}
                    className="p-4 bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-xl hover:shadow-lg transition-all"
                    data-testid={`user-card-${user.id}`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-1">
                          <h3 className="font-semibold text-gray-900">{user.name}</h3>
                          <Badge variant={user.role === 'admin' ? 'default' : user.role === 'driver' ? 'outline' : 'secondary'} 
                            className={user.role === 'driver' ? 'border-orange-500 text-orange-600' : ''}>
                            {user.role === 'driver' && <Truck className="w-3 h-3 mr-1" />}
                            {user.role}
                          </Badge>
                          {user.receive_orders && (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              Receives Orders
                            </Badge>
                          )}
                          {user.receive_order_copy && (
                            <Badge variant="outline" className="text-blue-600 border-blue-600">
                              Order Copy
                            </Badge>
                          )}
                          {user.receive_region_orders && (
                            <Badge variant="outline" className="text-orange-600 border-orange-600">
                              Region Orders
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{user.email}</p>
                        {hospital && (
                          <p className="text-sm text-gray-500">Hospital: {hospital.name}</p>
                        )}
                        {region && (
                          <p className="text-sm text-gray-500">Region: {region.name}</p>
                        )}
                        {(user.address || user.post_nr || user.city) && (
                          <p className="text-sm text-gray-500">
                            {user.address && `${user.address}, `}
                            {user.post_nr && `${user.post_nr} `}
                            {user.city}
                          </p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        {user.role === 'user' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleManageItems(user)}
                            data-testid={`manage-items-${user.id}`}
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Manage Items
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(user)}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(user.id)}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manage Items Dialog */}
      <Dialog open={manageItemsOpen} onOpenChange={setManageItemsOpen}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto" data-testid="manage-items-dialog">
          <DialogHeader>
            <DialogTitle>Manage Items for {selectedUser?.name}</DialogTitle>
            <DialogDescription>Enable or disable items for this user</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {Object.keys(groupedUserItems).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-lg">No items found for this user</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.keys(groupedUserItems).sort().map((vendorName) => (
                  <div key={vendorName} className="bg-white rounded-lg border border-gray-200">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                        <span className="w-1.5 h-6 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></span>
                        <span>{vendorName}</span>
                        <span className="text-sm font-normal text-gray-600">({groupedUserItems[vendorName].length} items)</span>
                      </h3>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {groupedUserItems[vendorName].sort((a, b) => a.item_name.localeCompare(b.item_name)).map((item) => (
                        <div
                          key={item.item_id}
                          className={`px-4 py-3 hover:bg-gray-50 transition-colors ${!item.is_assigned ? 'bg-blue-25 border-l-4 border-l-blue-400' : ''}`}
                          data-testid={`user-item-row-${item.item_id}`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex-1 grid grid-cols-4 gap-4">
                              <div>
                                <div className="flex items-center space-x-2">
                                  <p className="text-sm font-semibold text-gray-900">{item.item_name}</p>
                                  {!item.is_assigned && (
                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">New</span>
                                  )}
                                </div>
                                {item.item_description && (
                                  <p className="text-xs text-gray-500 mt-1">{item.item_description}</p>
                                )}
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">SKU</p>
                                <p className="text-sm text-gray-900 mt-1">{item.sku || '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Units</p>
                                <p className="text-sm text-gray-900 mt-1">{item.units || '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Order Count</p>
                                <p className="text-sm text-gray-900 mt-1">{item.order_count || 0}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3 ml-4">
                              <div className="flex items-center space-x-2">
                                <span className={`text-sm font-medium ${item.enabled ? 'text-green-600' : 'text-gray-500'}`}>
                                  {item.is_assigned ? (item.enabled ? 'Enabled' : 'Disabled') : 'Not Assigned'}
                                </span>
                                <Switch
                                  checked={item.enabled}
                                  onCheckedChange={() => toggleItemEnabled(item, item.enabled)}
                                  data-testid={`toggle-item-${item.item_id}`}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}